import { axios } from '@vyce/core/src/api';

import {
  GetResourcePermissionOverridesParams,
  ModulePermissionOverridesDTO,
  PermissionCatalogueModuleDTO,
  PermissionOverridesPreviewPayload,
  ResourcePermissionOverridesResponseDTO,
  UpdateModulePermissionOverridePayload,
  UpdateResourcePermissionOverridePayload,
} from '../types';
import config from '../config';

const URL = `${config.gatewayApiUrl}`;

// Labels and hints for the permission checkboxes. Needs
// management:can_read_permissions or management:can_manage_staff_roles.
export const getCompanyPermissionsCatalogueRequest = (companyId: string) => {
  return axios.get<PermissionCatalogueModuleDTO[]>(`${URL}/public/v1/companies/${companyId}/permissions`);
};

// 404 = the person holds no position in this company.
export const getUserPermissionOverridesRequest = (companyId: string, userId: string) => {
  return axios.get<ModulePermissionOverridesDTO[]>(
    `${URL}/public/v1/companies/${companyId}/users/${userId}/permission_overrides`
  );
};

// The body is the complete state: a module left out has its override cleared.
// An empty permissions array is NOT a reset — the server diffs the list against
// the role defaults, so [] records every default as removed and leaves the person
// with a role and no permissions. To go back to defaults, send the defaults.
export const updateUserPermissionOverridesRequest = (
  companyId: string,
  userId: string,
  payload: UpdateModulePermissionOverridePayload[]
) => {
  return axios.put<ModulePermissionOverridesDTO[]>(
    `${URL}/public/v1/companies/${companyId}/users/${userId}/permission_overrides`,
    payload
  );
};

// The staff-invite Customise form, for roles nobody holds yet. Without permissions
// the rows carry the role defaults; with them, what the invite would store after
// dependencies are pulled in — possibly in a module that wasn't sent. Writes
// nothing. Same gate as the per-user GET.
export const previewPermissionOverridesRequest = (
  companyId: string,
  payload: PermissionOverridesPreviewPayload
) => {
  return axios.post<ModulePermissionOverridesDTO[]>(
    `${URL}/public/v1/companies/${companyId}/permission_overrides/preview`,
    payload
  );
};

export const getUserResourcePermissionOverridesRequest = (
  companyId: string,
  userId: string,
  params: GetResourcePermissionOverridesParams
) => {
  const { modules, ...rest } = params;
  return axios.get<ResourcePermissionOverridesResponseDTO>(
    `${URL}/public/v1/companies/${companyId}/users/${userId}/permission_overrides/resources`,
    // The endpoint takes modules as one comma-separated value, so join it here
    // instead of letting the shared paramsSerializer repeat the key.
    { params: { ...rest, ...(modules?.length ? { modules: modules.join(',') } : {}) } }
  );
};

// No module in the path — the server derives it from the resource and reports it
// back next to module_default.
export const updateResourcePermissionOverrideRequest = (
  companyId: string,
  userId: string,
  resourceId: string,
  payload: UpdateResourcePermissionOverridePayload
) => {
  return axios.put(
    `${URL}/public/v1/companies/${companyId}/users/${userId}/permission_overrides/resources/${resourceId}`,
    payload
  );
};
